import { useEffect, useRef, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import TurnstileChallenge from "common/Captcha/TurnstileChallenge";
import { openMessage } from "utils/misc";
import type { UseGeetestProps } from "utils/hooks/useGeetest";
import {
  reportCaptchaFailure,
  type CaptchaFailureStage,
} from "utils/captchaScript";

export const hasUsableTurnstileSiteKey = (): boolean => {
  const siteKey = import.meta.env.VITE_APP_TURNSTILE_SITE_KEY;
  return !!siteKey?.trim() && !siteKey.includes("placeholder");
};

export const useTurnstile = (options: UseGeetestProps) => {
  const { isWhitelabel } = useBranding();
  const [validateCaptcha] =
    sharedLib.sharedApi.authentication.useLazyValidateCaptchaQuery();
  const [isLoading, setIsLoading] = useState(false);
  const attempt = useRef(0);
  const pending = useRef(false);
  const validating = useRef(false);
  const callbacks = useRef(options);
  callbacks.current = options;
  const sitekey = import.meta.env.VITE_APP_TURNSTILE_SITE_KEY;

  useEffect(
    () => () => {
      attempt.current += 1;
    },
    []
  );

  const finish = () => {
    attempt.current += 1;
    pending.current = false;
    validating.current = false;
    setIsLoading(false);
  };

  const fail = (
    stage: CaptchaFailureStage,
    errorCode?: string,
    status?: unknown
  ) => {
    reportCaptchaFailure("turnstile", stage, errorCode, status);
    const message = callbacks.current.captchaErrorMessage || DEFAULT_ERROR;
    finish();
    openMessage(message, "error");
    callbacks.current.onError?.(message);
  };

  const verify = () => {
    if (pending.current) return;
    if (!sitekey) {
      fail("configuration");
      return;
    }
    attempt.current += 1;
    pending.current = true;
    setIsLoading(true);
  };

  const validate = async (token: string) => {
    if (validating.current || !pending.current) return;
    validating.current = true;
    const currentAttempt = attempt.current;
    try {
      const result = await validateCaptcha(
        {
          provider: "turnstile",
          turnstile_token: token,
          is_whitelabel: isWhitelabel,
        },
        false
      ).unwrap();
      if (currentAttempt !== attempt.current) return;
      if (!result.success || !result.captcha_token) {
        fail("validation_rejected");
        return;
      }
      finish();
      callbacks.current.onSuccess(result.captcha_token);
    } catch (error) {
      if (currentAttempt === attempt.current) {
        const status =
          error && typeof error === "object" && "status" in error
            ? error.status
            : undefined;
        fail("validation_request", undefined, status);
      }
    }
  };

  return {
    verify,
    isLoading,
    captcha:
      isLoading && sitekey ? (
        <TurnstileChallenge
          sitekey={sitekey}
          onSuccess={validate}
          onError={fail}
        />
      ) : null,
  };
};

const DEFAULT_ERROR =
  "Something went wrong when validating captcha information, please try again";
